import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskItem, TaskList } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { useToast } from '../../context/ToastContext';
import { MATERIAL_STATUS, isMaterial, materialStatus, materialTotal, money, num } from '../../utils/projectValue';
import { PROJECT_MAX_DEPTH, childrenOf } from '../../utils/taskTree';
import TaskIcon from '../tasks/TaskIcon';
import ItemActions from './ItemActions';

type Group = 'task' | 'vendor' | 'status';

function readPref<T extends string>(key: string, allowed: T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* per-viewer convenience only */
  }
}

/** A cell edited in place: saves on Enter or when you click away, if it changed. */
const Cell: React.FC<{
  value: string;
  onSave: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
  readOnly: boolean;
  className?: string;
  label: string;
}> = ({ value, onSave, type = 'text', placeholder, readOnly, className = '', label }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    if (draft.trim() !== value.trim()) onSave(draft.trim());
  };
  return (
    <input
      className={`tp-m-cell ${className}`}
      type={type}
      min={type === 'number' ? 0 : undefined}
      step={type === 'number' ? 'any' : undefined}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      readOnly={readOnly}
      aria-label={label}
    />
  );
};

const numOrNull = (s: string) => (s === '' || !Number.isFinite(Number(s)) ? null : Math.max(0, Number(s)));

/**
 * The project's shopping list: every material, wherever it sits on the map.
 * Edit quantities, prices and suppliers in place; tap the status to move it
 * along Needed → Ordered → Received. It's only a reminder of what to get —
 * nothing here touches Purchase Orders.
 */
const MaterialsView: React.FC<{ project: TaskList; items: TaskItem[]; readOnly: boolean }> = ({ project, items, readOnly }) => {
  const { createItem, updateItem, showOnLists, lists, me, members } = useTasks();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [group, setGroupState] = useState<Group>(() => readPref('project.materialsGroup', ['task', 'vendor', 'status'], 'task'));
  const [scope, setScopeState] = useState<'open' | 'all'>(() => readPref('project.materialsScope', ['open', 'all'], 'open'));
  const setGroup = (g: Group) => {
    setGroupState(g);
    writePref('project.materialsGroup', g);
  };
  const setScope = (s: 'open' | 'all') => {
    setScopeState(s);
    writePref('project.materialsScope', s);
  };

  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const materials = useMemo(() => items.filter(isMaterial), [items]);
  const shown = materials.filter((m) => scope === 'all' || m.progress < 100);

  // Totals by status (all materials, whatever's filtered).
  const totals = MATERIAL_STATUS.map((s) => {
    const these = materials.filter((m) => materialStatus(m) === s.label);
    return { ...s, count: these.length, sum: these.reduce((t, m) => t + (materialTotal(m) ?? 0), 0) };
  });
  const grand = totals.reduce((t, s) => t + s.sum, 0);

  // Tasks a new material can be for, as an indented outline.
  const forOptions = useMemo(() => {
    const kids = childrenOf(items.filter((i) => !isMaterial(i)));
    const out: { id: number; label: string }[] = [];
    const walk = (i: TaskItem, depth: number) => {
      if (i.depth >= PROJECT_MAX_DEPTH) return;
      out.push({ id: i.id, label: `${'  '.repeat(depth)}${depth ? '› ' : ''}${i.text || 'Topic'}` });
      (kids.get(i.id) || []).forEach((c) => walk(c, depth + 1));
    };
    (kids.get(null) || []).forEach((i) => walk(i, 0));
    return out;
  }, [items]);

  const [draft, setDraft] = useState({ text: '', qty: '', price: '', vendor: '', forId: '' });
  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.text.trim();
    if (!text) return;
    const parentId = draft.forId ? Number(draft.forId) : null;
    createItem({
      list_id: project.id,
      parent_id: parentId,
      text,
      node_kind: 'material',
      qty: numOrNull(draft.qty),
      cost: numOrNull(draft.price),
      vendor: draft.vendor.trim() || null,
    });
    // Keep "for" and "from": the next thing is usually for the same job, from the same place.
    setDraft((d) => ({ ...d, text: '', qty: '', price: '' }));
  };

  const groupOf = (m: TaskItem): string => {
    if (group === 'vendor') return m.vendor || 'No supplier yet';
    if (group === 'status') return materialStatus(m);
    const parent = m.parent_id ? byId.get(m.parent_id) : undefined;
    return parent ? parent.text || 'Topic' : 'Not under a task';
  };
  const groups = useMemo(() => {
    const out = new Map<string, TaskItem[]>();
    const order = (m: TaskItem) => (m.progress >= 100 ? 2 : m.progress > 0 ? 1 : 0);
    [...shown]
      .sort((a, b) => order(a) - order(b) || a.sort_order - b.sort_order)
      .forEach((m) => {
        const g = groupOf(m);
        out.set(g, [...(out.get(g) || []), m]);
      });
    return Array.from(out.entries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, group, byId]);

  const cycle = (m: TaskItem) => updateItem(m.id, { progress: m.progress >= 100 ? 0 : m.progress > 0 ? 100 : 50 });

  const copyList = async () => {
    const open = materials.filter((m) => m.progress < 100);
    const byVendor = new Map<string, TaskItem[]>();
    open.forEach((m) => byVendor.set(m.vendor || 'Anywhere', [...(byVendor.get(m.vendor || 'Anywhere') || []), m]));
    const text = [
      `${project.title || 'Project'} — to buy`,
      ...Array.from(byVendor.entries()).flatMap(([vendor, list]) => [
        '',
        vendor,
        ...list.map((m) => {
          const total = materialTotal(m);
          return `- ${m.qty != null && m.qty !== 1 ? `${num(m.qty)} × ` : ''}${m.text}${total !== null ? ` (${money(total)})` : ''}${m.progress > 0 ? ' — ordered' : ''}${m.buy_url ? ` ${m.buy_url}` : ''}`;
        }),
      ]),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showToast('Shopping list copied', 'success');
    } catch {
      showToast("Couldn't copy — your browser blocked it", 'error');
    }
  };

  const myCard = lists.find(
    (l) => l.kind === 'view' && l.view_list_id === project.id && l.view_filter === 'materials' && !l.view_root_item_id &&
      (l.owner_email === me || (members.get(l.id) || []).includes(me))
  );

  return (
    <div className="tp-materials">
      <div className="tp-gantt-tools">
        <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Show">
          <button type="button" className={scope === 'open' ? 'is-active' : ''} onClick={() => setScope('open')}>To buy</button>
          <button type="button" className={scope === 'all' ? 'is-active' : ''} onClick={() => setScope('all')}>All</button>
        </div>
        <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Group by">
          <button type="button" className={group === 'task' ? 'is-active' : ''} onClick={() => setGroup('task')}>By task</button>
          <button type="button" className={group === 'vendor' ? 'is-active' : ''} onClick={() => setGroup('vendor')}>By supplier</button>
          <button type="button" className={group === 'status' ? 'is-active' : ''} onClick={() => setGroup('status')}>By status</button>
        </div>
        <span className="tp-m-actions">
          <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm" onClick={copyList} disabled={!materials.some((m) => m.progress < 100)}>
            <TaskIcon name="copy" size={13} /> Copy list
          </button>
          <button
            type="button"
            className="tk-btn tk-btn-ghost tk-btn-sm"
            onClick={async () => {
              if (myCard) navigate(`/lists?list=${myCard.id}`);
              else if (await showOnLists(project.id, null, 'materials')) showToast('The shopping list is on your Lists', 'success');
            }}
          >
            <TaskIcon name="list" size={13} /> {myCard ? 'On your Lists' : 'Show on my Lists'}
          </button>
        </span>
      </div>

      <div className="tp-m-totals">
        {totals.map((t) => (
          <span key={t.label} className={`tp-m-total is-${t.label.toLowerCase()}`}>
            <span>{t.label}</span>
            <strong>{money(t.sum)}</strong>
            <small>{t.count} item{t.count === 1 ? '' : 's'}</small>
          </span>
        ))}
        <span className="tp-m-total is-all">
          <span>Total</span>
          <strong>{money(grand)}</strong>
          <small>{materials.length} item{materials.length === 1 ? '' : 's'}</small>
        </span>
      </div>

      {!readOnly && (
        <form className="tp-m-add" onSubmit={add}>
          <TaskIcon name="cart" size={15} />
          <input className="tk-input tp-m-add-text" value={draft.text} placeholder="Something to buy" onChange={(e) => setDraft({ ...draft, text: e.target.value })} aria-label="What to buy" />
          <input className="tk-input tp-m-add-num" type="number" min={0} step="any" value={draft.qty} placeholder="Qty" onChange={(e) => setDraft({ ...draft, qty: e.target.value })} aria-label="How many" />
          <input className="tk-input tp-m-add-num" type="number" min={0} step="0.01" value={draft.price} placeholder="$ each" onChange={(e) => setDraft({ ...draft, price: e.target.value })} aria-label="Price each" />
          <input className="tk-input tp-m-add-vendor" value={draft.vendor} placeholder="From" onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} aria-label="Supplier" />
          <select className="tk-input tp-m-add-for" value={draft.forId} onChange={(e) => setDraft({ ...draft, forId: e.target.value })} aria-label="For which task">
            <option value="">For… (no task)</option>
            {forOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <button type="submit" className="tk-btn tk-btn-primary tk-btn-sm" disabled={!draft.text.trim()}>Add</button>
        </form>
      )}

      {groups.length === 0 ? (
        <div className="tp-empty">
          <TaskIcon name="cart" size={28} />
          <p>
            {materials.length
              ? 'Everything has been received.'
              : "Nothing to buy yet. Add it above, or open any topic's ⋯ › Something to buy."}
          </p>
        </div>
      ) : (
        groups.map(([name, list]) => {
          const subtotal = list.reduce((t, m) => t + (materialTotal(m) ?? 0), 0);
          return (
            <section key={name} className="tp-m-group">
              <h3 className="tp-m-group-head">
                <span>{name}</span>
                {subtotal > 0 && <span className="tk-muted">{money(subtotal)}</span>}
              </h3>
              <ul className="tp-m-list">
                {list.map((m) => {
                  const status = materialStatus(m);
                  const total = materialTotal(m);
                  const parent = m.parent_id ? byId.get(m.parent_id) : undefined;
                  return (
                    <li key={m.ckey || m.id} className={`tp-m-row is-${status.toLowerCase()}`}>
                      <button
                        type="button"
                        className={`tp-m-status is-${status.toLowerCase()}`}
                        onClick={() => !readOnly && cycle(m)}
                        disabled={readOnly}
                        title="Needed → Ordered → Received"
                      >
                        {status}
                      </button>
                      <Cell className="tp-m-name" value={m.text} onSave={(v) => v && updateItem(m.id, { text: v })} readOnly={readOnly} label="Item" />
                      <Cell className="tp-m-num" type="number" value={m.qty != null ? String(m.qty) : ''} placeholder="1" onSave={(v) => updateItem(m.id, { qty: numOrNull(v) })} readOnly={readOnly} label="How many" />
                      <span className="tp-m-x">×</span>
                      <Cell className="tp-m-num" type="number" value={m.cost != null ? String(m.cost) : ''} placeholder="$" onSave={(v) => updateItem(m.id, { cost: numOrNull(v) })} readOnly={readOnly} label="Price each" />
                      <span className="tp-m-sum">{total !== null ? money(total) : '—'}</span>
                      {group !== 'vendor' && (
                        <Cell className="tp-m-vendor" value={m.vendor || ''} placeholder="From" onSave={(v) => updateItem(m.id, { vendor: v || null })} readOnly={readOnly} label="Supplier" />
                      )}
                      {group !== 'task' && <span className="tp-m-for" title={parent?.text}>{parent?.text || ''}</span>}
                      {m.buy_url ? (
                        <a className="tk-icon-btn tk-icon-btn-sm tp-m-link" href={m.buy_url} target="_blank" rel="noopener noreferrer" title="Open the link">
                          <TaskIcon name="link" size={13} />
                        </a>
                      ) : (
                        <span className="tp-m-link" />
                      )}
                      <ItemActions item={m} readOnly={readOnly} hasChildren={false} onAddChild={() => undefined} maxPeople={1} canAddChild={false} />
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
};

export default MaterialsView;
